
import { Component, Vue } from "vue-property-decorator";
import * as Models from "@gigalot/data-models";
import DimssaButton, { ButtonState } from "@/components/shared/dimssa-button.vue";
import { changeTrackingComponentFragment } from "@/helpers/gql";
import { getItemFromServer } from "@/helpers/get-item-from-server";

type DispatchBatchListItem = {
  guid: string;
  reference: string;
  dispatchType: string;
  numAnimals?: number;
  dispatchedAnimalsCount?: number;
  getDispatchedAnimalsButtonState?: ButtonState;
  updateBatchButtonState?: ButtonState;
};

@Component({
  components: {
    DimssaButton,
  },
})
export default class FixDispatchBatches extends Vue {
  dispatchBatches: DispatchBatchListItem[] = [];
  dabsByGuid: Record<string, Models.DispatchToAbattoirBatch> = {};
  getDispatchBatchesButtonState: ButtonState = "ready";
  getDispatchBatchesErrorMessage = "";

  created() {
    this.$store.commit("navFuncs", { save: undefined, back: undefined });
  }

  async getDispatchBatchesFromCsv() {
    this.getDispatchBatchesButtonState = "busy";
    this.getDispatchBatchesErrorMessage = "";
    try {
      const gql = `
        query getDispatchBatchesFromCsv($guid: String!) {
          getDispatchBatchesFromCsv(guid: $guid)
        }
      `;
      const json = await this.$store.dispatch(
        "graphQl",
        {
          gql,
          variables: {
            guid: (this.$store.state as any).user.location.guid,
          },
        },
        { root: true }
      );

      console.log(json.data.getDispatchBatchesFromCsv);
      this.dispatchBatches = (JSON.parse(json.data.getDispatchBatchesFromCsv) || [])
        .sort((a: DispatchBatchListItem, b: DispatchBatchListItem) => (a.reference || "").localeCompare(b.reference || ""))
        .map((dispatchBatch: DispatchBatchListItem) => ({
          ...dispatchBatch,
          getDispatchedAnimalsButtonState: "ready",
          updateBatchButtonState: "ready",
        }));
      this.getDispatchBatchesButtonState = "success";
    } catch (err) {
      console.error("getDispatchBatchesFromCsv error:", err);
      this.getDispatchBatchesButtonState = "error";
      this.getDispatchBatchesErrorMessage = err instanceof Error ? err.message : String(err);
    }
  }

  async getDispatchBatch(dispatchBatchListItem: DispatchBatchListItem): Promise<Models.DispatchToAbattoirBatch> {
    return await getItemFromServer("DispatchToAbattoirBatch", dispatchBatchListItem.guid);
  }

  async getDispatchBatchAndAnimals(dispatchBatch: DispatchBatchListItem) {
    const dab = await this.getDispatchBatch(dispatchBatch);
    const animals = await this.getDispatchedAnimals(dispatchBatch);
    if (!animals) return;
    dab.dispatchedAnimals = animals;
    this.dabsByGuid = {
      ...this.dabsByGuid,
      [dab.guid]: dab,
    };
  }

  async updateBatch(dispatchBatch: DispatchBatchListItem) {
    dispatchBatch.updateBatchButtonState = "busy";
    try {
      const dab = this.dabsByGuid[dispatchBatch.guid];
      if (!dab) throw Error(`No DispatchToAbattoirBatch loaded for ${dispatchBatch.guid}`);

      dab.metadata = this.$store.getters["user/getUpstreamMetadata"]();

      const json = await this.$store.dispatch(
        "graphQl",
        {
          gql: `mutation dispatchToAbattoirBatch($guid: String!, $dispatchToAbattoirBatch: DispatchToAbattoirBatchInput!) {
            dispatchToAbattoirBatch(guid: $guid, dispatchToAbattoirBatch: $dispatchToAbattoirBatch) {
              ...changeTrackingFields
            }
          }${changeTrackingComponentFragment}`,
          variables: {
            guid: (this.$store.state as any).user.location.guid,
            dispatchToAbattoirBatch: dab,
          },
          timeout: 10 * 1000,
        },
        { root: true }
      );

      dab.changeTracking = json.data.dispatchToAbattoirBatch;
      this.dabsByGuid = {
        ...this.dabsByGuid,
        [dab.guid]: dab,
      };
      dispatchBatch.updateBatchButtonState = "success";
    } catch (err) {
      console.log("updateBatch error: ", err);
      dispatchBatch.updateBatchButtonState = "error";
    }
  }

  async getDispatchedAnimals(dispatchBatch: DispatchBatchListItem) {
    dispatchBatch.getDispatchedAnimalsButtonState = "busy";
    try {
      const gql = `
        query dispatchedAnimals($guid: String!, $dispatchBatchGuid: String!, $dispatchBatchReference: String!, $type: String!) {
          dispatchedAnimals(
            guid: $guid,
            dispatchBatchGuid: $dispatchBatchGuid,
            dispatchBatchReference: $dispatchBatchReference,
            type: $type)
          {
            sgtin
            breed
            gender
            owner
            dispatchTime
            dispatchMassIndividual
            dispatchMassWeighbridge
            abattoirMassWeighbridge
            dispatchType
            dispatchReference
            referenceGuid
          }
        }
      `;
      const json = await this.$store.dispatch(
        "graphQl",
        {
          gql,
          variables: {
            guid: (this.$store.state as any).user.location.guid,
            dispatchBatchGuid: dispatchBatch.guid,
            dispatchBatchReference: dispatchBatch.reference,
            type: "abattoir",
          },
        },
        { root: true }
      );

      if (!json.data || !json.data.dispatchedAnimals) throw Error("No dispatchedAnimals returned.");
      dispatchBatch.dispatchedAnimalsCount = json.data.dispatchedAnimals.length;
      console.dir(json.data.dispatchedAnimals);
      dispatchBatch.getDispatchedAnimalsButtonState = "success";
      return json.data.dispatchedAnimals;
    } catch (err) {
      console.log("getDispatchedAnimals error: ", err);
      dispatchBatch.getDispatchedAnimalsButtonState = "error";
    }
  }
}
